import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { api } from "../lib/api";

// Paystack redirects here after payment. We poll our verify endpoint (webhook is the backup
// source of truth), so a slow confirmation never shows a scary "failed" error.
export default function PaymentCallback() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const reference = sp.get("reference") || sp.get("trxref");
  const [state, setState] = useState({ status: "loading", msg: "Confirming your payment…" });

  useEffect(() => {
    if (!reference) {
      setState({ status: "pending", msg: "If you completed payment, your order will appear in your account shortly." });
      return;
    }
    let cancelled = false;
    let tries = 0;
    const MAX = 12;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/paystack/verify/${reference}`);
        if (data.payment_status === "paid") {
          navigate(`/payment/success?order=${encodeURIComponent(data.order_number)}`, { replace: true });
          return;
        }
      } catch {
        // transient — keep polling; the Paystack webhook confirms server-side regardless
      }
      tries += 1;
      if (tries < MAX && !cancelled) {
        setTimeout(poll, 2500);
      } else if (!cancelled) {
        setState({
          status: "pending",
          msg: "Payment received — we're just finalising your confirmation. You'll get an email and it'll show in your orders within a few minutes.",
        });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [reference, navigate]);

  const Icon = state.status === "pending" ? Clock : state.status === "ok" ? CheckCircle2 : Loader2;
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-5" data-testid="payment-callback">
      <div className="bg-white border border-black/5 rounded-3xl p-10 text-center max-w-sm w-full">
        <Icon size={40} className={`mx-auto mb-4 text-[#306D29] ${state.status === "loading" ? "animate-spin" : ""}`} />
        <p className="font-display text-2xl text-[#0D530E] mb-2">{state.status === "pending" ? "Almost there" : "Payment"}</p>
        <p className="text-[#5C5C5C]" data-testid="callback-msg">{state.msg}</p>
        {state.status === "pending" && (
          <button onClick={() => navigate("/account")} data-testid="callback-orders-btn"
            className="mt-5 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold px-6 py-2.5 rounded-full"
            style={{ transition: "background-color 0.2s ease" }}>View my orders</button>
        )}
      </div>
    </div>
  );
}
